import { useEffect, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { navItems } from '@/components/feature/Footer';

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);

  const submitSearch = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : '/search');
  };

  const isActive = (to: string) =>
    to === '/' ? location.pathname === '/' : location.pathname.startsWith(to.split('?')[0]);

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur border-b border-background-200'
          : 'bg-background-50/80 backdrop-blur border-b border-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-10 h-16 md:h-[72px] flex items-center gap-4 md:gap-8">
        <Link to="/" className="flex items-center gap-2.5 shrink-0 group">
          <span className="w-9 h-9 md:w-10 md:h-10 rounded-lg bg-primary-700 text-background-50 flex items-center justify-center transition-transform group-hover:scale-105">
            <i className="ri-film-line text-lg md:text-xl"></i>
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-base md:text-lg font-semibold tracking-wide text-foreground-950">
              伦理片在线观看
            </span>
            <span className="hidden sm:block text-[10px] tracking-[0.22em] text-foreground-500 mt-1">
              ETHICS CINEMA
            </span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1 ml-2">
          {navItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`px-3 py-2 text-sm rounded-md transition-colors whitespace-nowrap ${
                isActive(item.to)
                  ? 'text-primary-700 bg-primary-50 font-medium'
                  : 'text-foreground-700 hover:text-primary-700 hover:bg-background-100'
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <form onSubmit={submitSearch} className="hidden md:flex items-center ml-auto">
          <div className="flex items-center h-10 rounded-full border border-background-300 bg-background-100 focus-within:border-primary-400 focus-within:bg-background-50 transition-colors overflow-hidden w-[200px] lg:w-[280px]">
            <span className="w-10 h-10 flex items-center justify-center text-foreground-500">
              <i className="ri-search-line text-base"></i>
            </span>
            <input
              type="text"
              name="q"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索剧名、演员、导演"
              aria-label="站内搜索"
              className="flex-1 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 outline-none pr-1"
            />
            <button
              type="submit"
              className="h-8 mr-1 px-3.5 rounded-full bg-primary-700 text-background-50 text-xs whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
            >
              搜索
            </button>
          </div>
        </form>

        <div className="ml-auto md:ml-0 flex items-center gap-2">
          <Link
            to="/search"
            aria-label="搜索"
            className="md:hidden w-10 h-10 rounded-lg flex items-center justify-center text-foreground-800 hover:bg-background-100"
          >
            <i className="ri-search-line text-lg"></i>
          </Link>
          <button
            type="button"
            aria-label="打开菜单"
            onClick={() => setMenuOpen((v) => !v)}
            className="lg:hidden w-10 h-10 rounded-lg flex items-center justify-center text-foreground-900 hover:bg-background-100 cursor-pointer"
          >
            <i className={menuOpen ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'}></i>
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="lg:hidden fixed inset-x-0 top-16 z-40 bg-background-50 border-b border-background-200 animate-fade-in max-h-[calc(100vh-4rem)] overflow-y-auto">
          <div className="px-4 py-4">
            <form onSubmit={submitSearch} className="mb-4">
              <div className="flex items-center h-11 rounded-lg border border-background-300 bg-background-100 overflow-hidden focus-within:border-primary-400">
                <span className="w-11 h-11 flex items-center justify-center text-foreground-500">
                  <i className="ri-search-line"></i>
                </span>
                <input
                  type="text"
                  name="q_mobile"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="搜索剧名、演员、导演"
                  aria-label="站内搜索"
                  className="flex-1 bg-transparent text-sm outline-none placeholder:text-foreground-400"
                />
                <button
                  type="submit"
                  className="h-9 mr-1 px-4 rounded-md bg-primary-700 text-background-50 text-xs whitespace-nowrap cursor-pointer"
                >
                  搜索
                </button>
              </div>
            </form>
            <div className="grid grid-cols-2 gap-2">
              {navItems.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className={`h-12 rounded-lg flex items-center px-4 text-sm transition-colors ${
                    isActive(item.to)
                      ? 'bg-primary-700 text-background-50'
                      : 'bg-background-100 text-foreground-800'
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}